import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FaTimes, FaFilter, FaChevronRight, FaChevronDown } from 'react-icons/fa';
import { parseDepRef, toSubtaskId } from '../../../utils/dependencyRefs';
import { getNumericId } from '../../../utils/idHelpers';
import { effortLabel, isOverrun } from '../../../utils/effortDisplay';

// Every bordered control in the header shares this shadow, offset down and
// right so the whole app agrees on where the light comes from (see the card
// shadow note in docs/08-KANBAN-BOARD.md). Lighter and smaller than a card's,
// because these sit flat rather than lifting.
const CONTROL_SHADOW = '1px 2px 4px rgba(0,0,0,0.08), 1px 1px 2px rgba(0,0,0,0.05)';

// "Admin" is a placeholder account, never a real assignee.
const stripPlaceholder = (list) => (list || []).filter(m =>
    m.name?.toLowerCase() !== 'admin' &&
    m.email?.toLowerCase() !== 'admin'
);

/**
 * ⛔ `showEffort` DEFAULTS TO FALSE, AND THAT DEFAULT IS THE POINT (T476/D4).
 *
 * This modal renders in BOTH portals - `ClientPortalLayout.js:235` as well as
 * the admin `ClientView.js` - so an unconditional effort column would put
 * admin-only figures in front of a client. The server strip (T466) means the
 * values never arrive there anyway; this is the second gate, and it fails
 * closed.
 */
const AllTasksModal = ({ isOpen, onClose, apiUrl, apiFetch, fetchUrl, members: propMembers, adminMembers: propAdminMembers, onQuickAssign, refreshKey, blockedTasksMap = new Map(), showEffort = false }) => {
    const [tasks, setTasks] = useState([]);
    // The fetch already returns these; they were being thrown away. `links`
    // gives the successors for the hover, `rawTasks` the names behind a
    // dependency reference (including subtasks whose parent this table skips).
    const [links, setLinks] = useState([]);
    const [rawTasks, setRawTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [savingTaskId, setSavingTaskId] = useState(null);
    const [openAssigneeDropdown, setOpenAssigneeDropdown] = useState(null);
    const [lastRefreshKey, setLastRefreshKey] = useState(refreshKey);

    // Get storage key based on lead ID
    const getStorageKey = (suffix) => {
        // Extract leadId from fetchUrl or localStorage
        let leadId = localStorage.getItem('clientLeadId');
        if (fetchUrl) {
            const match = fetchUrl.match(/leadId=([^&]+)/);
            if (match) leadId = decodeURIComponent(match[1]);
        }
        return `allTasks_${suffix}_${leadId || 'unknown'}`;
    };

    // Load persisted state from localStorage
    const loadPersistedState = () => {
        try {
            const savedTeamFilter = localStorage.getItem(getStorageKey('teamFilter'));
            const savedExpandedTasks = localStorage.getItem(getStorageKey('expandedTasks'));
            const savedFilters = localStorage.getItem(getStorageKey('filters'));

            return {
                teamFilter: savedTeamFilter || 'all',
                expandedTasks: savedExpandedTasks ? new Set(JSON.parse(savedExpandedTasks)) : new Set(),
                filters: savedFilters ? JSON.parse(savedFilters, (key, value) => {
                    // Convert arrays back to Sets for filter values
                    if (key && Array.isArray(value)) return new Set(value);
                    return value;
                }) : null
            };
        } catch (e) {
            console.error('Error loading AllTasks persisted state:', e);
            return { teamFilter: 'all', expandedTasks: new Set(), filters: null };
        }
    };

    // Initialise with persisted state
    const persistedState = loadPersistedState();

    // Filter states - each is a Set of selected values (empty = show all)
    const [filters, setFilters] = useState(persistedState.filters || {
        status: new Set(),
        swimlane: new Set(),
        assignee: new Set(),
        dueDate: new Set(),
        type: new Set()
    });

    // Track which filter dropdown is open
    const [openFilter, setOpenFilter] = useState(null);

    // Search term
    const [searchTerm, setSearchTerm] = useState('');

    // Track expanded main tasks (by ID)
    const [expandedTasks, setExpandedTasks] = useState(persistedState.expandedTasks);

    // Team filter: 'all', 'client', 'ours'
    const [teamFilter, setTeamFilter] = useState(persistedState.teamFilter);

    // Hide completed main tasks (persisted, defaults to showing them).
    // Scoped by lead via getStorageKey, matching this file's other persisted
    // state, so one client's preference cannot leak into another's portal on a
    // shared browser.
    const [hideCompleted, setHideCompleted] = useState(() => {
        return localStorage.getItem(getStorageKey('hideCompleted')) === 'true';
    });
    useEffect(() => {
        localStorage.setItem(getStorageKey('hideCompleted'), hideCompleted.toString());
    }, [hideCompleted]);

    // Blocked-only filter (persisted, defaults to off). Scoped by lead via
    // getStorageKey, matching this file's other persisted state.
    const [blockedOnly, setBlockedOnly] = useState(() => {
        return localStorage.getItem(getStorageKey('blockedOnly')) === 'true';
    });
    useEffect(() => {
        localStorage.setItem(getStorageKey('blockedOnly'), blockedOnly.toString());
    }, [blockedOnly]);

    // Blocked hover panel. Fixed position with a z above this modal's own
    // overlay (z-50) so the scrolling table cannot clip it.
    const [blockedHover, setBlockedHover] = useState(null); // { x, y, sections }

    useEffect(() => {
        if (isOpen) {
            fetchTasks();
        } else {
            // Reset non-persisted state when modal closes
            setTasks([]);
            setLinks([]);
            setRawTasks([]);
            setOpenFilter(null);
            setSearchTerm('');
            setError(null);
            // A panel left open at close would otherwise reappear at its old
            // fixed position the next time the modal opens.
            setBlockedHover(null);
            // Note: filters, expandedTasks, teamFilter are persisted - don't reset
        }
    }, [isOpen]);

    // Persist teamFilter to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(getStorageKey('teamFilter'), teamFilter);
        } catch (e) {
            console.error('Error saving teamFilter:', e);
        }
    }, [teamFilter]);

    // Persist expandedTasks to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(getStorageKey('expandedTasks'), JSON.stringify([...expandedTasks]));
        } catch (e) {
            console.error('Error saving expandedTasks:', e);
        }
    }, [expandedTasks]);

    // Persist filters to localStorage
    useEffect(() => {
        try {
            // Convert Sets to arrays for JSON serialisation
            const filtersForStorage = {
                status: [...filters.status],
                swimlane: [...filters.swimlane],
                assignee: [...filters.assignee],
                dueDate: [...filters.dueDate],
                type: [...filters.type]
            };
            localStorage.setItem(getStorageKey('filters'), JSON.stringify(filtersForStorage));
        } catch (e) {
            console.error('Error saving filters:', e);
        }
    }, [filters]);

    // Refresh when refreshKey changes (WebSocket update) while modal is open
    useEffect(() => {
        if (isOpen && refreshKey !== lastRefreshKey) {
            setLastRefreshKey(refreshKey);
            fetchTasks();
        }
    }, [isOpen, refreshKey, lastRefreshKey]);

    // Close filter dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (openFilter && !e.target.closest('.filter-dropdown')) {
                setOpenFilter(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openFilter]);

    const fetchTasks = async () => {
        setLoading(true);
        setError(null);
        try {
            let url;
            if (fetchUrl) {
                url = fetchUrl;
            } else {
                const leadId = localStorage.getItem('clientLeadId');
                const timestamp = localStorage.getItem('clientTimestamp');
                url = `${apiUrl}/client/project?leadId=${encodeURIComponent(leadId)}&timestamp=${encodeURIComponent(timestamp)}`;
            }

            const response = await apiFetch(url);

            if (!response.ok) {
                throw new Error('Failed to fetch tasks');
            }

            const data = await response.json();

            setLinks(data.links || []);
            setRawTasks(data.tasks || []);

            // Process tasks - group subtasks under main tasks
            const processedTasks = processTaskData(data);
            setTasks(processedTasks);
        } catch (err) {
            console.error('Error fetching tasks:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const processTaskData = (data) => {
        const { tasks: allTasks = [], project = {}, members = [] } = data;
        const swimlanes = project.swimlanes || [];
        const projectName = project.name || '';

        // Assignments are stored by EMAIL (assignedTo: "jane@example.com"), so
        // that is the key. Lead id is added as a second key for the same member
        // because both are unique and records written by older paths hold one or
        // the other. /admin/team-members sets leadId to the email, which is why
        // admin-assigned rows resolved while client-assigned ones did not.
        const memberLookup = {};
        members.forEach(m => {
            const label = m.name || m.email;
            if (!label) return;
            if (m.email) memberLookup[m.email] = label;
            if (m.leadId) memberLookup[m.leadId] = label;
        });

        // Create swimlane lookup - format Team swimlane with project name (like KanbanBoard)
        const swimlaneLookup = {};
        swimlanes.forEach(s => {
            if (s.name === 'Team' && projectName) {
                swimlaneLookup[s.swimlaneId] = `${projectName}'s Team`;
            } else {
                swimlaneLookup[s.swimlaneId] = s.name;
            }
        });

        // Main tasks sit in an epic and carry no statusName; subtasks carry a
        // denormalised statusName and no epic. One column shows both, hence
        // the "Epic/Status" header. epicId is numeric on both sides, but the
        // key is coerced anyway so a template's string ids cannot miss.
        const epicLookup = {};
        (project.epics || []).forEach(e => {
            epicLookup[String(e.epicId)] = e.name;
        });

        // Separate main tasks and subtasks
        const mainTasks = allTasks.filter(t => !t.mainTaskId);
        const subtasks = allTasks.filter(t => t.mainTaskId);

        // Create subtask lookup by parent ID
        const subtasksByParent = {};
        subtasks.forEach(st => {
            const parentId = st.mainTaskId;
            if (!subtasksByParent[parentId]) {
                subtasksByParent[parentId] = [];
            }
            subtasksByParent[parentId].push(st);
        });

        // Build flat list with subtasks under parents
        const result = [];
        mainTasks.forEach(task => {
            const taskSwimlane = swimlaneLookup[task.swimlaneId] || 'Unknown';

            // Skip tasks with Unknown swimlane
            if (taskSwimlane === 'Unknown') return;

            // Check if this is a client task (Team swimlane)
            const swimlaneName = swimlanes.find(s => s.swimlaneId === task.swimlaneId)?.name || '';
            const isClientTask = swimlaneName === 'Team';

            // Add main task
            result.push({
                id: task.taskId,
                name: task.subject,
                // Raw epicId when the epic cannot be found: an epic holding tasks
                // cannot be deleted (admin-epics returns 409), so this should be
                // unreachable - and if it is reached, the id is what identifies
                // the problem.
                status: epicLookup[String(task.epicId)] || (task.epicId != null ? String(task.epicId) : ''),
                swimlane: taskSwimlane,
                // Main tasks hold a list. This read `assignedUsersFullName`, a field
                // written nowhere and present in no record, so main-task assignees
                // never appeared here (Bill, 2026-08-03). `assignedTo` is kept as a
                // fallback for records written by the old quick-assign path.
                assignees: (task.assignedUsersNames && task.assignedUsersNames.length > 0)
                    ? task.assignedUsersNames
                    : (task.assignedUsers && task.assignedUsers.length > 0)
                        ? task.assignedUsers.map(u => memberLookup[u] || u)
                        : (task.assignedTo ? [memberLookup[task.assignedTo] || task.assignedTo] : []),
                dueDate: task.dueDate,
                type: 'Main Task',
                isSubtask: false,
                parentId: null,
                originalTask: task,
                isClosed: task.isClosed === true,
                isClientTask: isClientTask,
                // ⚠️ T476 ADDED THE EFFORT COLUMN AND ITS CELL BUT NOT THESE TWO
                // LINES, so the cell read `task.effortEstimated` off a row that
                // never carried it and the column showed a dash on EVERY row.
                // Its own tests asserted the column HEADER only, so it passed.
                // Found while building ECL-08 (2026-09-02), which gates the same
                // cell on the lane - a gate over a cell that renders nothing is
                // a check that cannot fail.
                //
                // ⚠️ MAIN TASKS ONLY. A subtask cannot carry effort - the write
                // path refuses it - so the subtask push below deliberately has
                // no equivalent.
                effortEstimated: task.effortEstimated,
                effortActual: task.effortActual
            });

            // Add subtasks for this main task
            const taskSubtasks = subtasksByParent[task.taskId] || [];
            taskSubtasks.forEach(st => {
                result.push({
                    id: st.taskId,
                    name: st.subject,
                    status: st.statusName || 'Unknown',
                    swimlane: taskSwimlane, // Inherit from parent
                    assignees: st.assignedToName
                        ? [st.assignedToName]
                        : (st.assignedTo ? [memberLookup[st.assignedTo] || st.assignedTo] : []),
                    dueDate: st.dueDate,
                    type: 'Sub Task',
                    isSubtask: true,
                    parentId: task.taskId,
                    originalTask: st,
                    isClosed: st.isClosed === true,
                    isClientTask: isClientTask
                });
            });
        });

        return result;
    };

    // Get unique values for each filter column
    const filterOptions = useMemo(() => {
        const options = {
            status: new Set(),
            swimlane: new Set(),
            assignee: new Set(),
            dueDate: new Set(),
            type: new Set()
        };

        tasks.forEach(task => {
            if (task.status) options.status.add(task.status);
            if (task.swimlane) options.swimlane.add(task.swimlane);
            task.assignees.forEach(a => options.assignee.add(a));
            options.dueDate.add(task.dueDate || 'No due date');
            options.type.add(task.type);
        });

        return {
            status: Array.from(options.status).sort(),
            swimlane: Array.from(options.swimlane).sort(),
            assignee: Array.from(options.assignee).sort(),
            dueDate: Array.from(options.dueDate).sort((a, b) => {
                if (a === 'No due date') return 1;
                if (b === 'No due date') return -1;
                return new Date(a) - new Date(b);
            }),
            type: Array.from(options.type).sort()
        };
    }, [tasks]);

    // Filter tasks based on current filters and search term
    const filteredTasks = useMemo(() => {
        // Completed main tasks and everything under them drop out when the
        // toggle is on. `tasks` is a flat list with subtasks following their
        // parent, so hidden parents are collected first and their children
        // removed by parent id.
        const hiddenParentIds = hideCompleted
            ? new Set(tasks.filter(t => !t.isSubtask && t.isClosed === true).map(t => t.id))
            : null;

        return tasks.filter(task => {
            if (hiddenParentIds) {
                if (!task.isSubtask && task.isClosed === true) return false;
                if (task.isSubtask && hiddenParentIds.has(task.parentId)) return false;
            }
            // Search filter - check all columns
            if (searchTerm && searchTerm.trim()) {
                const search = searchTerm.trim().toLowerCase();
                const taskName = (task.name || '').toLowerCase();
                const taskStatus = (task.status || '').toLowerCase();
                const taskSwimlane = (task.swimlane || '').toLowerCase();
                const assigneeNames = (task.assignees || []).join(' ').toLowerCase();
                const taskDueDate = (task.dueDate || '').toLowerCase();
                const taskType = (task.type || '').toLowerCase();

                if (!taskName.includes(search) &&
                    !taskStatus.includes(search) &&
                    !taskSwimlane.includes(search) &&
                    !assigneeNames.includes(search) &&
                    !taskDueDate.includes(search) &&
                    !taskType.includes(search)) {
                    return false;
                }
            }
            // Status filter
            if (filters.status.size > 0 && !filters.status.has(task.status)) {
                return false;
            }
            // Swimlane filter
            if (filters.swimlane.size > 0 && !filters.swimlane.has(task.swimlane)) {
                return false;
            }
            // Assignee filter
            if (filters.assignee.size > 0) {
                const hasMatchingAssignee = task.assignees.some(a => filters.assignee.has(a));
                if (task.assignees.length === 0 && !filters.assignee.has('Unassigned')) {
                    return false;
                }
                if (task.assignees.length > 0 && !hasMatchingAssignee) {
                    return false;
                }
            }
            // Due date filter
            if (filters.dueDate.size > 0) {
                const dueDateValue = task.dueDate || 'No due date';
                if (!filters.dueDate.has(dueDateValue)) {
                    return false;
                }
            }
            // Type filter
            if (filters.type.size > 0 && !filters.type.has(task.type)) {
                return false;
            }
            return true;
        });
    }, [tasks, filters, searchTerm, hideCompleted]);

    // Subtasks the table would show if their parent were open: everything the
    // search box, column filters and Hide completed left (filteredTasks), less
    // archived rows and the team toggle. Expansion is deliberately NOT applied -
    // it is a fold, not a filter, so a collapsed parent still gets its bar.
    // The team toggle cannot split a parent from its children (isClientTask is
    // inherited), but a search term or an archived status can.
    const eligibleSubtasks = useMemo(() => filteredTasks.filter(t =>
        t.isSubtask &&
        t.status?.toLowerCase() !== 'archived' &&
        (teamFilter === 'all' || (teamFilter === 'client' ? t.isClientTask : !t.isClientTask))
    ), [filteredTasks, teamFilter]);

    // Blocked state per row. Only subtasks can be blocked (docs/10-BLOCKING.md)
    // and a closed subtask never counts. Main tasks roll their children up so
    // blocking stays visible while the parent is collapsed - but only children
    // the current filters left standing, so a bar never points at a row the
    // filters have taken away.
    const blockedInfo = useMemo(() => {
        const bySubtask = new Map();   // subtask id -> { blockers, hasIssue }
        const byParent = new Map();    // main task id -> { count, hasIssue }

        eligibleSubtasks.forEach(task => {
            if (task.isClosed) return;
            const blockers = blockedTasksMap.get(String(task.id)) || [];
            if (blockers.length === 0) return;

            const hasIssue = blockers.some(b => b.type === 'issue');
            bySubtask.set(String(task.id), { blockers, hasIssue });

            const parentKey = String(task.parentId);
            const roll = byParent.get(parentKey) || { count: 0, hasIssue: false };
            roll.count += 1;
            roll.hasIssue = roll.hasIssue || hasIssue;   // issue style wins
            byParent.set(parentKey, roll);
        });

        return { bySubtask, byParent };
    }, [eligibleSubtasks, blockedTasksMap]);

    // Task lookups for dependency names. Built from the raw payload rather than
    // the table rows: a dependency can point at a subtask whose parent this
    // table skipped (unknown swimlane), and it still deserves a name.
    const subtaskById = useMemo(() => {
        const map = new Map();
        (rawTasks || []).forEach(t => { if (t.mainTaskId) map.set(String(t.taskId), t); });
        return map;
    }, [rawTasks]);

    const mainTaskById = useMemo(() => {
        const map = new Map();
        (rawTasks || []).forEach(t => { if (!t.mainTaskId) map.set(String(t.taskId), t); });
        return map;
    }, [rawTasks]);

    const describeTask = (record) => {
        if (!record) return null;
        const parent = mainTaskById.get(String(record.mainTaskId));
        return {
            label: parent ? `${parent.subject} / ${record.subject}` : record.subject,
            isClosed: record.isClosed === true
        };
    };

    // Predecessors come from the row's own dependsOn entries, successors from
    // the links array the API already built. Both are FS.
    const getDependencyDetail = (task) => {
        if (!task?.isSubtask) return { predecessors: [], successors: [] };

        const raw = task.originalTask?.dependsOn;
        const predecessors = (typeof raw === 'string' ? raw.split(',') : [])
            .map(e => e.trim())
            .filter(e => e)
            .map(entry => {
                const parsed = parseDepRef(entry);
                // Non-canonical entries are surfaced, never silently skipped
                // (dependencyRefs.js contract).
                if (parsed.invalid) {
                    return { label: `Unrecognised reference (${parsed.raw})`, isClosed: false };
                }
                const id = toSubtaskId(parsed.numericRef);
                return describeTask(subtaskById.get(id)) || { label: id, isClosed: false };
            });

        const ownRef = String(getNumericId(task.id));
        const successors = (links || [])
            .filter(l => (!l.type || l.type === 'FS') && String(l.source) === ownRef)
            .map(l => describeTask(subtaskById.get(String(l.target)))
                || { label: String(l.target), isClosed: false });

        return { predecessors, successors };
    };

    const showBlockedHover = (e, payload) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setBlockedHover({ x: rect.left + rect.width / 2, y: rect.top - 8, ...payload });
    };
    const hideBlockedHover = () => setBlockedHover(null);

    const formatBlocker = (b) => b.type === 'issue'
        ? `Issue ${b.id}: ${b.title}`
        : (b.parentTitle ? `${b.parentTitle} / ${b.title}` : b.title);

    // Blocked subtask: what stops it, what it waits on, what waits on it.
    const buildSubtaskHover = (task, blockers) => {
        const { predecessors, successors } = getDependencyDetail(task);
        return {
            sections: [
                { heading: 'Blocked by:', tone: 'red', lines: blockers.map(formatBlocker) },
                ...(predecessors.length > 0 ? [{
                    heading: 'Depends on:',
                    tone: 'neutral',
                    lines: predecessors.map(p => `${p.label} (${p.isClosed ? 'done' : 'open'})`)
                }] : []),
                ...(successors.length > 0 ? [{
                    heading: 'Blocks:',
                    tone: 'neutral',
                    lines: successors.map(s => `${s.label} (${s.isClosed ? 'done' : 'open'})`)
                }] : [])
            ]
        };
    };

    // Main task: a rollup of its blocked children. Dependency lists belong to a
    // specific subtask, so they are not repeated here.
    const buildMainTaskHover = (mainTaskId) => {
        const children = tasks.filter(t =>
            t.isSubtask &&
            String(t.parentId) === String(mainTaskId) &&
            blockedInfo.bySubtask.has(String(t.id))
        );
        return {
            sections: [{
                heading: `${children.length} blocked subtask${children.length === 1 ? '' : 's'}:`,
                tone: 'red',
                lines: children.flatMap(child => [
                    child.name,
                    ...blockedInfo.bySubtask.get(String(child.id)).blockers
                        .map(b => `    ${formatBlocker(b)}`)
                ])
            }]
        };
    };

    // Rows actually rendered: team filter, archived exclusion, blocked-only and
    // parent expansion. Extracted from the tbody so the empty state and the
    // header count both reflect what is on screen.
    // Row-level checks every candidate must pass, whatever admitted it.
    const passesRowChecks = useCallback((task) => {
        // Filter out archived statuses
        if (task.status?.toLowerCase() === 'archived') return false;
        // Apply team filter
        if (teamFilter === 'client' && !task.isClientTask) return false;
        if (teamFilter === 'ours' && task.isClientTask) return false;
        return true;
    }, [teamFilter]);

    // ISS-031: parents NOT in filteredTasks whose subtask survived. They are
    // drawn as CONTEXT rows so the hierarchy stays readable, and forced open
    // (same rule as blockedOnly) so the surviving match is never hidden behind
    // a chevron that guards nothing.
    const contextParentIds = useMemo(() => {
        if (blockedOnly) return new Set();
        const filteredMainIds = new Set(
            filteredTasks.filter(t => !t.isSubtask).map(t => String(t.id))
        );
        const out = new Set();
        filteredTasks.forEach(t => {
            if (!t.isSubtask) return;
            if (!passesRowChecks(t)) return;
            if (!filteredMainIds.has(String(t.parentId))) out.add(String(t.parentId));
        });
        return out;
    }, [filteredTasks, passesRowChecks, blockedOnly]);

    const visibleRows = useMemo(() => {
        // Blocked only: blocked subtasks, plus the main tasks holding them.
        // Parents are forced open in the row render, so no expansion check here.
        if (blockedOnly) {
            return filteredTasks.filter(task => {
                if (!passesRowChecks(task)) return false;
                return task.isSubtask
                    ? blockedInfo.bySubtask.has(String(task.id))
                    : blockedInfo.byParent.has(String(task.id));
            });
        }

        // ISS-031: a row's own match counts. filteredTasks applies the column
        // filters to each level independently, so a main task can survive with
        // no surviving children and a subtask can survive without its parent.
        // Walk `tasks` (the canonical parent-then-children order) so context
        // parents land immediately before their subtasks.
        const filteredSet = new Set(filteredTasks);
        const filteredMainIds = new Set(
            filteredTasks.filter(t => !t.isSubtask).map(t => String(t.id))
        );
        const rows = [];
        tasks.forEach(task => {
            if (!task.isSubtask) {
                if (!passesRowChecks(task)) return;
                const id = String(task.id);
                if (filteredMainIds.has(id)) {
                    // Self-match. A main task with no subtasks AT ALL is still
                    // never listed (that is a rule, not an accident) - but
                    // children filtered OUT no longer hide a parent that
                    // matched on its own account.
                    if (tasks.some(t => t.isSubtask && String(t.parentId) === id)) {
                        rows.push(task);
                    }
                } else if (contextParentIds.has(id)) {
                    rows.push(task);
                }
                return;
            }
            if (!filteredSet.has(task)) return;
            if (!passesRowChecks(task)) return;
            // A subtask renders when its parent is expanded, or when the
            // parent is context-only and therefore forced open (ISS-031).
            if (contextParentIds.has(String(task.parentId)) || expandedTasks.has(task.parentId)) {
                rows.push(task);
            }
        });
        return rows;
    }, [tasks, filteredTasks, contextParentIds, passesRowChecks, blockedOnly, blockedInfo, expandedTasks]);

    const toggleFilterValue = (column, value) => {
        setFilters(prev => {
            const newSet = new Set(prev[column]);
            if (newSet.has(value)) {
                newSet.delete(value);
            } else {
                newSet.add(value);
            }
            return { ...prev, [column]: newSet };
        });
    };

    const clearFilter = (column) => {
        setFilters(prev => ({ ...prev, [column]: new Set() }));
    };

    const selectAllFilter = (column) => {
        setFilters(prev => ({ ...prev, [column]: new Set(filterOptions[column]) }));
    };

    const formatDateUK = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB');
    };

    const getDueDateColour = (dateString) => {
        if (!dateString) return 'text-gray-400';
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dueDate = new Date(dateString);
        dueDate.setHours(0, 0, 0, 0);
        const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return 'text-red-600';
        if (diffDays <= 2) return 'text-orange-500';
        return 'text-green-600';
    };

    // Handle quick assign. Email is the identifier everywhere (Bill, 2026-08-03) -
    // this used to pass leadId, which the handler then compared against emails, so the
    // lookup failed and a raw leadId was stored as the assignee's name.
    const handleAssign = async (taskId, userEmail, isSubtask, memberList) => {
        if (!onQuickAssign) return;
        setSavingTaskId(taskId);
        setOpenAssigneeDropdown(null);
        try {
            await onQuickAssign(taskId, userEmail, isSubtask);

            // Update local state instead of full refetch
            const selectedMember = userEmail
                ? (memberList || []).find(m => String(m.email) === String(userEmail))
                : null;
            const name = selectedMember?.name || selectedMember?.email || null;
            setTasks(prevTasks => prevTasks.map(task => {
                if (String(task.id) === String(taskId)) {
                    // Mirror what the server now stores: a list on main tasks, the
                    // single field on subtasks
                    const assignmentFields = isSubtask
                        ? { assignedTo: userEmail || null, assignedToName: name }
                        : {
                            assignedUsers: userEmail ? [userEmail] : [],
                            assignedUsersNames: name ? [name] : [],
                            assignedToName: null
                        };

                    return {
                        ...task,
                        assignees: selectedMember ? [name] : [],
                        originalTask: {
                            ...task.originalTask,
                            ...assignmentFields
                        }
                    };
                }
                return task;
            }));
        } finally {
            setSavingTaskId(null);
        }
    };

    // Close assignee dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (openAssigneeDropdown && !e.target.closest('.assignee-dropdown')) {
                setOpenAssigneeDropdown(null);
            }
        };
        if (openAssigneeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openAssigneeDropdown]);

    const clientMembers = useMemo(() => stripPlaceholder(propMembers), [propMembers]);
    const adminMembers = useMemo(() => stripPlaceholder(propAdminMembers), [propAdminMembers]);

    // A Team task is the client's to own, a Migration Experts task is ours.
    // Rows inherit isClientTask from their main task, so a subtask resolves the
    // same list as its parent - the rule the Epics section applies via
    // isInTMESwimlane.
    const membersForTask = (task) => (task.isClientTask ? clientMembers : adminMembers);

    // Assignee dropdown cell component
    const AssigneeDropdownCell = ({ task }) => {
        const originalTask = task.originalTask;
        const isOpen = openAssigneeDropdown === task.id;
        const isSaving = savingTaskId === task.id;
        const rowMembers = membersForTask(task);

        // Main tasks label from the list; subtasks from their single field. Main tasks
        // no longer carry assignedToName, so reading it alone showed nothing.
        const currentAssignee = (!task.isSubtask && originalTask?.assignedUsersNames?.length > 0)
            ? originalTask.assignedUsersNames[0]
            : (originalTask?.assignedToName || null);

        // If task is closed, show strikethrough text (no dropdown)
        if (task.isClosed) {
            return currentAssignee ? (
                <span className="text-gray-400 line-through">{currentAssignee}</span>
            ) : (
                <span className="text-gray-400 line-through">Unassigned</span>
            );
        }

        // If no onQuickAssign prop, just show static text
        if (!onQuickAssign) {
            return task.assignees.length > 0
                ? task.assignees.join(', ')
                : <span className="text-gray-400">Unassigned</span>;
        }

        if (isSaving) {
            return (
                <div className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-500">Saving...</span>
                </div>
            );
        }

        return (
            <div className="assignee-dropdown relative">
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenAssigneeDropdown(isOpen ? null : task.id);
                    }}
                    className="hover:text-primary-600 hover:underline cursor-pointer text-left"
                >
                    {currentAssignee || <span className="text-gray-400">Unassigned</span>}
                </button>

                {isOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[180px]">
                        <button
                            onClick={() => handleAssign(task.id, null, task.isSubtask, rowMembers)}
                            className="w-full px-3 py-2 text-left text-sm text-gray-500 hover:bg-gray-100 border-b"
                        >
                            Unassigned
                        </button>
                        {rowMembers.map(member => (
                            <button
                                key={member.leadId}
                                onClick={() => !member.disabled && handleAssign(task.id, member.email, task.isSubtask, rowMembers)}
                                disabled={member.disabled === true}
                                title={member.disabled ? 'User is disabled' : undefined}
                                className={`w-full px-3 py-2 text-left text-sm ${member.disabled ? 'text-gray-400 cursor-not-allowed' : 'hover:bg-gray-100 text-gray-700'}`}
                                style={(originalTask?.assignedTo === member.leadId || originalTask?.assignedTo === member.email) ? { background: 'linear-gradient(to bottom, #fef9c3 0%, #fefce8 100%)' } : undefined}
                            >
                                {member.name || member.email}{member.disabled ? ' (disabled)' : ''}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Blue (client task) and red (blocked) indicator bars, in one fixed 13px
    // slot rendered on every row - blue 3px, 2px gap, red up to 8px - so blocked,
    // client, both and neither all line up and nothing shifts between states
    // (docs/08-KANBAN-BOARD.md, constant indicator width).
    //
    // Blue now appears on subtask rows too, not just main tasks, so a blocked
    // client row shows both colours instead of red appearing to replace blue.
    // Red: issue-blocked draws the Epics double line (3px + 2px gap + 3px),
    // dependency-blocked a single 4px line.
    const IndicatorBars = ({ isClientTask, blocked, onMouseEnter, onMouseLeave }) => (
        <span className="flex items-center flex-shrink-0 mr-2" style={{ width: '13px' }}>
            <span className="flex-shrink-0" style={{ width: '3px' }}>
                {isClientTask && (
                    <span className="block h-4 rounded bg-blue-500" style={{ width: '3px' }} />
                )}
            </span>
            <span className="flex-shrink-0" style={{ width: '2px' }} />
            <span
                className={`flex items-center flex-shrink-0 ${blocked ? 'cursor-help' : ''}`}
                style={{ width: '8px' }}
                onMouseEnter={blocked ? onMouseEnter : undefined}
                onMouseLeave={blocked ? onMouseLeave : undefined}
            >
                {blocked && (blocked.hasIssue ? (
                    <>
                        <span className="h-4 rounded" style={{ width: '3px', backgroundColor: '#dc2626' }} />
                        <span style={{ width: '2px' }} />
                        <span className="h-4 rounded" style={{ width: '3px', backgroundColor: '#dc2626' }} />
                    </>
                ) : (
                    <span className="h-4 rounded" style={{ width: '4px', backgroundColor: '#dc2626' }} />
                ))}
            </span>
        </span>
    );

    const FilterDropdown = ({ column, label }) => {
        const isOpen = openFilter === column;
        const hasActiveFilter = filters[column].size > 0;
        const options = filterOptions[column];

        return (
            <div className="filter-dropdown relative inline-flex items-center">
                <span>{label}</span>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        setOpenFilter(isOpen ? null : column);
                    }}
                    className={`ml-1 p-1 rounded hover:bg-gray-200 ${hasActiveFilter ? 'text-primary-600' : 'text-gray-400'}`}
                    title={`Filter by ${label}`}
                >
                    <FaFilter size={10} />
                </button>
                {isOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded shadow-lg z-50 min-w-[200px] max-h-[300px] overflow-auto">
                        <div className="p-2 border-b border-gray-200 flex gap-2">
                            <button
                                onClick={() => selectAllFilter(column)}
                                className="text-xs text-primary-600 hover:underline"
                            >
                                Select All
                            </button>
                            <button
                                onClick={() => clearFilter(column)}
                                className="text-xs text-primary-600 hover:underline"
                            >
                                Clear
                            </button>
                        </div>
                        <div className="p-2">
                            {options.map(option => (
                                <label key={option} className="flex items-center gap-2 py-1 px-2 hover:bg-gray-50 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={filters[column].has(option)}
                                        onChange={() => toggleFilterValue(column, option)}
                                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    <span className="text-sm text-gray-700">
                                        {column === 'dueDate' && option !== 'No due date'
                                            ? formatDateUK(option)
                                            : option}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[calc(80vh-3px)] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    {/* Title and key are one group: justify-between would
                        otherwise push a third child out to the centre. */}
                    <div className="flex items-center gap-5 flex-shrink-0">
                    <h2 className="text-lg font-semibold text-gray-900">
                        All Tasks
                        {!loading && <span className="text-sm font-normal text-gray-500 ml-2">({visibleRows.length})</span>}
                    </h2>
                    {/* Key - swatches are the row indicators at their real
                        widths, so what is described is what is drawn. Styling
                        follows the Gantt's colour key. */}
                    <div
                        className="flex items-center gap-3 rounded border border-gray-200 px-3 py-1.5"
                        style={{ boxShadow: CONTROL_SHADOW }}
                    >
                        <span className="text-sm leading-4 font-semibold text-gray-500">Key:</span>
                        <span className="flex items-center gap-1">
                            <span className="h-4 rounded bg-blue-500" style={{ width: '3px' }} />
                            <span className="text-sm leading-4 text-gray-600">Client</span>
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="h-4 rounded" style={{ width: '4px', backgroundColor: '#dc2626' }} />
                            <span className="text-sm leading-4 text-gray-600">Blocked</span>
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="flex items-center" style={{ width: '8px' }}>
                                <span className="h-4 rounded" style={{ width: '3px', backgroundColor: '#dc2626' }} />
                                <span style={{ width: '2px' }} />
                                <span className="h-4 rounded" style={{ width: '3px', backgroundColor: '#dc2626' }} />
                            </span>
                            <span className="text-sm leading-4 text-gray-600">Issue Blocked</span>
                        </span>
                    </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex rounded border border-gray-300 overflow-hidden" style={{ boxShadow: CONTROL_SHADOW }}>
                            <button
                                onClick={() => setTeamFilter('all')}
                                className={`text-sm px-3 py-1.5 ${teamFilter === 'all' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setTeamFilter('ours')}
                                className={`text-sm px-3 py-1.5 border-l border-gray-300 ${teamFilter === 'ours' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                                T.M.E.
                            </button>
                            <button
                                onClick={() => setTeamFilter('client')}
                                className={`text-sm px-3 py-1.5 border-l border-gray-300 ${teamFilter === 'client' ? 'bg-primary-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                            >
                                Client
                            </button>
                        </div>
                        <button
                            onClick={() => setHideCompleted(!hideCompleted)}
                            className={`text-sm px-3 py-1.5 rounded border transition-colors ${
                                hideCompleted
                                    ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
                                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                            style={{ boxShadow: CONTROL_SHADOW }}
                            title={hideCompleted ? 'Show completed main tasks' : 'Hide completed main tasks'}
                        >
                            Hide completed
                        </button>
                        <button
                            onClick={() => setBlockedOnly(!blockedOnly)}
                            className={`text-sm px-3 py-1.5 rounded border transition-colors ${
                                blockedOnly
                                    ? 'border-primary-600 bg-primary-600 text-white hover:bg-primary-700'
                                    : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                            style={{ boxShadow: CONTROL_SHADOW }}
                            title={blockedOnly ? 'Show all tasks' : 'Show blocked tasks only'}
                        >
                            Blocked only
                        </button>
                        <button
                            onClick={() => {
                                const mainTaskIds = tasks.filter(t => !t.isSubtask).map(t => t.id);
                                if (expandedTasks.size > 0) {
                                    // Collapse all
                                    setExpandedTasks(new Set());
                                } else {
                                    // Expand all
                                    setExpandedTasks(new Set(mainTaskIds));
                                }
                            }}
                            className="text-sm px-3 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded border border-gray-300"
                            style={{ boxShadow: CONTROL_SHADOW }}
                        >
                            {expandedTasks.size > 0 ? 'Collapse All' : 'Expand All'}
                        </button>
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary-500 focus:border-primary-500 w-48"
                        />
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 p-1"
                        >
                            <FaTimes size={18} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
                            <span className="ml-3 text-sm text-gray-500">Loading tasks...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-16">
                            <p className="text-red-600 text-sm">{error}</p>
                            <button
                                onClick={fetchTasks}
                                className="mt-3 px-4 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700"
                            >
                                Retry
                            </button>
                        </div>
                    ) : tasks.length === 0 ? (
                        <div className="text-center py-16 text-sm text-gray-500">No tasks found.</div>
                    ) : (
                        <table className="w-full table-fixed">
                            <thead className="bg-gray-50 sticky top-0 border-b border-gray-200">
                                <tr>
                                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${showEffort ? 'w-[22%]' : 'w-[25%]'}`}>
                                        Name
                                    </th>
                                    {/* ⚠️ WIDTHS MUST TOTAL 100%, IN BOTH SHAPES.
                                        At 92% the browser spread the spare 8%
                                        across every column instead of honouring
                                        the stated share - so the Effort column
                                        (T476) REBALANCES the others rather than
                                        being appended to them.
                                          without: 25+21+18+15+11+10 = 100
                                          with:    22+18+16+13+11+10+10 = 100 */}
                                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${showEffort ? 'w-[18%]' : 'w-[21%]'}`}>
                                        <FilterDropdown column="status" label="Epic/Status" />
                                    </th>
                                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${showEffort ? 'w-[16%]' : 'w-[18%]'}`}>
                                        <FilterDropdown column="swimlane" label="Responsibility" />
                                    </th>
                                    <th className={`px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider ${showEffort ? 'w-[13%]' : 'w-[15%]'}`}>
                                        <FilterDropdown column="assignee" label="Assignee" />
                                    </th>
                                    {/* Sized to the date it holds - 10 characters
                                        plus the header's filter button. */}
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[11%]">
                                        <FilterDropdown column="dueDate" label="Due Date" />
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[10%]">
                                        <FilterDropdown column="type" label="Type" />
                                    </th>
                                    {showEffort && (
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-[10%]">
                                            Effort
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {/* ISS-032: the empty state keeps the header row
                                    above it, so the filter that emptied the table
                                    can still be cleared from here. */}
                                {visibleRows.length === 0 ? (
                                    <tr>
                                        <td colSpan={showEffort ? 7 : 6} className="text-center py-16 text-sm text-gray-500">
                                            {blockedOnly ? 'No blocked tasks.' : 'No tasks match the current filters.'}
                                        </td>
                                    </tr>
                                ) : visibleRows.map((task, index) => {
                                    // Blocked state: the row's own blockers for a subtask,
                                    // its children's rollup for a main task.
                                    const subtaskBlocked = task.isSubtask
                                        ? blockedInfo.bySubtask.get(String(task.id))
                                        : null;
                                    const parentRoll = !task.isSubtask
                                        ? blockedInfo.byParent.get(String(task.id))
                                        : null;
                                    const blockedState = subtaskBlocked || parentRoll || null;
                                    const hoverPayload = () => task.isSubtask
                                        ? buildSubtaskHover(task, subtaskBlocked.blockers)
                                        : buildMainTaskHover(task.id);
                                    const onBlockedEnter = blockedState
                                        ? (e) => showBlockedHover(e, hoverPayload())
                                        : undefined;

                                    // Check if this main task has subtasks (handle both string and numeric IDs)
                                    const hasSubtasks = !task.isSubtask && filteredTasks.some(t => String(t.parentId) === String(task.id));
                                    // Context rows are forced open like blocked-only
                                    // parents (ISS-031): the subtask is the match, so
                                    // there is nothing to toggle.
                                    const isContextRow = !task.isSubtask && contextParentIds.has(String(task.id));
                                    // Blocked-only forces every parent open, so there is
                                    // nothing to toggle while it is on.
                                    const isExpanded = blockedOnly || isContextRow || expandedTasks.has(task.id);

                                    return (
                                    <tr
                                        key={`${task.type}-${task.id}`}
                                        className={`${task.isSubtask ? (index % 2 === 0 ? 'bg-white' : 'bg-slate-50') : ''} hover:bg-yellow-50 transition-colors`}
                                        style={!task.isSubtask ? {
                                            // Whose task it is reads from the whole row, not only
                                            // from the 3px bar: blue for the client's Team tasks,
                                            // green for ours, the pairing the Gantt rows already
                                            // use (GanttChart.js:2106, bg-blue-50 vs bg-green-50).
                                            // The green is pitched lighter than the blue on
                                            // purpose - green reads heavier at the same value.
                                            background: task.isClientTask
                                                ? 'linear-gradient(to bottom, #dbeafe 0%, #eff6ff 100%)'
                                                : 'linear-gradient(to bottom, #e8f8ee 0%, #f5fdf8 100%)'
                                        } : undefined}
                                    >
                                        {/* The rule is painted on the cell, not the inner flex row,
                                            so it spans the full row height and reads as one
                                            continuous line down a group rather than a dashed one. */}
                                        <td
                                            className="px-6 py-3 text-sm overflow-hidden"
                                            style={task.isSubtask ? {
                                                backgroundImage: 'linear-gradient(to right, transparent 0, transparent 32px, #e5e7eb 32px, #e5e7eb 33px, transparent 33px)'
                                            } : undefined}
                                        >
                                            <div className={`flex items-center ${task.isSubtask ? 'pl-10' : ''}`}>
                                                {!task.isSubtask && hasSubtasks && !blockedOnly && !isContextRow && (
                                                    <button
                                                        onClick={() => {
                                                            const newExpanded = new Set(expandedTasks);
                                                            if (isExpanded) {
                                                                newExpanded.delete(task.id);
                                                            } else {
                                                                newExpanded.add(task.id);
                                                            }
                                                            setExpandedTasks(newExpanded);
                                                        }}
                                                        className="text-gray-500 hover:text-gray-700 mr-2 flex-shrink-0"
                                                    >
                                                        {isExpanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}
                                                    </button>
                                                )}
                                                {!task.isSubtask && (!hasSubtasks || blockedOnly || isContextRow) && <span className="w-4 mr-2 flex-shrink-0"></span>}
                                                <IndicatorBars
                                                    isClientTask={task.isClientTask}
                                                    blocked={blockedState}
                                                    onMouseEnter={onBlockedEnter}
                                                    onMouseLeave={hideBlockedHover}
                                                />
                                                <button
                                                    onClick={() => {
                                                        window.dispatchEvent(new CustomEvent('openTaskFromLinks', { detail: { taskId: task.id } }));
                                                    }}
                                                    onMouseEnter={onBlockedEnter}
                                                    onMouseLeave={blockedState ? hideBlockedHover : undefined}
                                                    className={`text-primary-600 hover:text-primary-800 hover:underline text-left truncate ${task.isClosed ? 'line-through text-gray-400' : ''}`}
                                                    title={blockedState ? undefined : task.name}
                                                >
                                                    {task.name}
                                                </button>
                                            </div>
                                        </td>
                                        <td className="px-6 py-3 text-sm text-gray-700">
                                            {task.status}
                                        </td>
                                        <td className="px-6 py-3 text-sm text-gray-700">
                                            {task.swimlane}
                                        </td>
                                        <td className="px-6 py-3 text-sm text-gray-700">
                                            <AssigneeDropdownCell task={task} />
                                        </td>
                                        <td className={`px-6 py-3 text-sm whitespace-nowrap ${getDueDateColour(task.dueDate)}`}>
                                            {formatDateUK(task.dueDate)}
                                        </td>
                                        <td className="px-6 py-3 text-sm text-gray-600">
                                            {task.type}
                                        </td>
                                        {/* ⚠️ THE COLUMN STAYS, ONLY THE CELL EMPTIES
                                            (D1, ECL-08). A column that came and went
                                            per row would reflow the whole table.
                                            `isClientTask` is the flag this file already
                                            derives from the lane NAME and puts on every
                                            main task and subtask - subtasks inherit it
                                            from their parent. */}
                                        {showEffort && (
                                            <td className="px-6 py-3 text-sm whitespace-nowrap">
                                                {!task.isClientTask
                                                    && effortLabel(task.effortEstimated, task.effortActual) ? (
                                                    <span className={
                                                        isOverrun(task.effortEstimated, task.effortActual)
                                                            ? 'text-red-600 font-semibold' : 'text-gray-600'
                                                    }>
                                                        {effortLabel(task.effortEstimated, task.effortActual)}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* Blocked hover panel - fixed, above this modal's own overlay,
                    styling matched to Gantt/Epics/TaskDetailsModal */}
                {blockedHover && (
                    <div
                        className="fixed z-[60] px-3 py-2 text-sm bg-gray-800 text-white rounded-lg max-w-sm pointer-events-none"
                        style={{
                            left: blockedHover.x,
                            top: blockedHover.y,
                            transform: 'translate(-50%, -100%)',
                            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.2)'
                        }}
                    >
                        {blockedHover.sections.map((section, si) => (
                            <div key={si} className={si > 0 ? 'mt-2' : ''}>
                                <div className={`font-bold ${section.tone === 'red' ? 'text-red-400' : 'text-gray-300'}`}>
                                    {section.heading}
                                </div>
                                {section.lines.map((line, li) => (
                                    <div
                                        key={li}
                                        className={`pl-2 whitespace-pre-wrap ${section.tone === 'red' ? 'text-red-300' : 'text-gray-100'}`}
                                    >
                                        {line}
                                    </div>
                                ))}
                            </div>
                        ))}
                        <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800" />
                    </div>
                )}
            </div>
        </div>
    );
};

export default AllTasksModal;
